import axios from 'axios';

class ClaudeAIService {
  constructor() {
    this.apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;
    this.baseURL = 'https://api.anthropic.com';
    // ✅ UPDATED: Use latest supported Claude model
    this.defaultModel = 'claude-3-5-haiku-20241022'; // Faster, more reliable model
    this.fallbackModel = 'claude-3-haiku-20240307'; // Fallback model
    this.maxTokens = 1500;
    this.temperature = 0.7; // ✅ OPTIMIZED: Better balance for professional responses
    
    // Session memory for conversation history
    this.sessionMemory = new Map();
    this.maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    };
  }

  isConfigured() {
    return this.apiKey && this.apiKey !== 'your_vite_anthropic_api_key';
  }

  // ✅ ENHANCED: Conversation memory with session pinning
  updateSessionMemory(sessionId, userMessage, assistantResponse, databaseResults = null) {
    if (!sessionId) return;
    
    const sessionData = this.sessionMemory?.get(sessionId) || { 
      conversations: [], 
      lastAccessed: Date.now(),
      sessionFacts: {} // ✅ NEW: Pin important session facts
    };
    
    // ✅ NEW: Extract and pin key conversation facts
    const facts = this.extractSessionFacts(userMessage, assistantResponse, sessionData?.sessionFacts);
    sessionData.sessionFacts = { ...sessionData?.sessionFacts, ...facts };
    
    // Add user message with metadata
    sessionData?.conversations?.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      database_results: databaseResults,
      session_facts: facts
    });
    
    // Add assistant response with context
    sessionData?.conversations?.push({
      role: 'assistant', 
      content: assistantResponse,
      timestamp: new Date(),
      applied_facts: Object.keys(facts)
    });
    
    // Keep only last 25 messages but preserve session facts
    if (sessionData?.conversations?.length > 25) {
      sessionData.conversations = sessionData?.conversations?.slice(-25);
    }
    
    sessionData.lastAccessed = Date.now();
    this.sessionMemory?.set(sessionId, sessionData);
    
    console.log('💾 Session memory updated:', {
      sessionId: sessionId?.slice(0, 8) + '...',
      messageCount: sessionData?.conversations?.length,
      sessionFacts: Object.keys(sessionData?.sessionFacts)
    });
  }

  // ✅ NEW: Extract and pin key conversation facts
  extractSessionFacts(userMessage, assistantResponse, existingFacts = {}) {
    const facts = {};
    const userLower = userMessage?.toLowerCase();
    const assistantLower = assistantResponse?.toLowerCase();
    
    // Extract names mentioned
    const namePattern = /(?:looking for|friend|person|name.*is|called)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/gi;
    let nameMatch;
    while ((nameMatch = namePattern?.exec(userMessage)) !== null) {
      const fullName = nameMatch?.[1]?.trim();
      if (fullName && fullName?.length > 2) {
        facts.search_subject = fullName;
        const nameParts = fullName?.split(' ');
        if (nameParts?.length > 1) {
          facts.first_name = nameParts?.[0];
          facts.last_name = nameParts?.[nameParts?.length - 1];
        }
      }
    }
    
    // Extract relationships
    if (userLower?.includes('friend')) facts.relationship = 'friend';
    if (userLower?.includes('son') || userLower?.includes('daughter')) facts.relationship = 'child';
    if (userLower?.includes('brother') || userLower?.includes('sister')) facts.relationship = 'sibling';
    
    // Extract locations/facilities
    if (userLower?.includes('sacramento')) facts.county = 'Sacramento';
    if (userLower?.includes('main jail')) facts.facility = 'Sacramento County Main Jail';
    if (userLower?.includes('rio') || userLower?.includes('cosumnes')) facts.facility = 'Rio Cosumnes Correctional';
    
    // Preserve existing important facts
    return { ...existingFacts, ...facts };
  }

  // ✅ NEW: Session Memory Management
  getSessionMemory(sessionId) {
    if (!sessionId) return [];
    
    const sessionData = this.sessionMemory?.get(sessionId);
    if (!sessionData) return [];
    
    // Check if session has expired
    if (Date.now() - sessionData?.lastAccessed > this.maxSessionAge) {
      this.sessionMemory?.delete(sessionId);
      return [];
    }
    
    return sessionData?.conversations || [];
  }

  // ✅ NEW: Format conversation messages with database context
  formatConversationWithDatabaseContext(sessionId, currentUserMessage, databaseResults = null) {
    const conversationHistory = this.getSessionMemory(sessionId);
    const messages = [];

    // Add previous conversation messages (limit to recent messages to stay within token limits)
    const recentHistory = conversationHistory?.slice(-10) || [];
    
    for (const msg of recentHistory) {
      if (msg?.role === 'user') {
        messages?.push({
          role: 'user',
          content: msg?.content
        });
      } else if (msg?.role === 'assistant') {
        messages?.push({
          role: 'assistant', 
          content: msg?.content
        });
      }
    }

    // Add current user message with database context
    let currentMessageContent = currentUserMessage;
    
    // If we have database results, include them in the message context
    if (databaseResults && Array.isArray(databaseResults) && databaseResults?.length > 0) {
      currentMessageContent += `\n\n[DATABASE_CONTEXT: Found ${databaseResults?.length} matching records in the system]`;
    } else if (databaseResults !== null) {
      currentMessageContent += `\n\n[DATABASE_CONTEXT: No matching records found in the system]`;
    }

    messages?.push({
      role: 'user',
      content: currentMessageContent
    });

    return messages;
  }

  // ✅ ENHANCED: Database-First System Prompt with pinned session facts
  getDatabaseFirstSystemPrompt(context = {}) {
    const { 
      agentName = 'Damian Sailors', 
      agentPhone = '(916) 833-0052', 
      sessionContext = {},
      conversationHistory = [],
      databaseResults = null
    } = context;
    
    // Get session facts for conversation continuity
    const sessionData = this.sessionMemory?.get(sessionContext?.sessionId);
    const sessionFacts = sessionData?.sessionFacts || {};
    
    return `You are ${agentName}, a professional bail bond agent for Sailors Bail Bonds in Sacramento County, California.

========================================
🧠 SESSION MEMORY & PINNED FACTS 🧠
========================================
**CRITICAL: You have perfect memory of this conversation session. NEVER ask for information already provided.**

${Object.keys(sessionFacts)?.length > 0 ? `**PINNED SESSION FACTS:**
${Object.entries(sessionFacts)?.map(([key, value]) => `• ${key}: ${value}`)?.join('\n')}

**CONVERSATION CONTINUITY RULES:**
- BUILD UPON these facts in every response
- DO NOT ask for information already established
- REFERENCE previous discussion naturally
- MAINTAIN context of ongoing searches or processes` : '**No established session facts yet.**'}

========================================
🚨 CRITICAL DATABASE-FIRST RULES 🚨
========================================
**ALWAYS use the inmate/releasee database provided in the context to answer custody and bail questions.**
**DO NOT send users to external websites, phone numbers, or third-party systems.**
**If the database has no match, respond with: "I wasn't able to find them in my system. Would you like me to connect you with Damian directly at ${agentPhone}?"**

**NEVER:**
- Direct users to sacsheriff.com or any other website
- Tell users to "check this website" or "visit this link"
- Provide external phone numbers other than ${agentPhone}
- Suggest users go elsewhere for information
- Ask for information already established in session facts

**ALWAYS:**
- Use database results provided as structured facts
- Ground all responses in the provided database data
- Build upon established session facts and context
- Maintain conversation flow and continuity
- Reference previous searches or information naturally

========================================
CONVERSATION MEMORY & CONTEXT
========================================
- REMEMBER everything discussed in this conversation session
- USE pinned session facts to maintain context
- BUILD UPON previous interactions and information provided
- DO NOT repeat questions about established facts
- PROGRESS conversations logically based on session history
- REFERENCE ongoing processes or searches naturally

========================================
DATABASE RESULTS INTEGRATION
========================================
${databaseResults ? `
**CURRENT DATABASE RESULTS:**
${JSON.stringify(databaseResults, null, 2)}

Use these results as FACTS. Present them in a clear, structured format. If someone was found, provide:
- Full name and basic details
- Current custody status
- Facility location
- Bail amount (if available)  
- Charges (if available)
- Clear next steps for bail process
` : '**No current database results available.**'}

Remember: Your goal is to use database information and session memory to help people navigate bail bonds efficiently while maintaining perfect conversation continuity throughout the entire interaction.`;
  }

  // ✅ ENHANCED: Main message enhancement with database-first approach
  async enhanceMessage(userMessage, context = {}) {
    if (!this.isConfigured()) {
      console.warn('Claude API key not configured, using database-first fallback');
      return this.getDatabaseFirstFallback(userMessage, context);
    }

    try {
      const { sessionContext = {}, databaseResults = null, conversationHistory = [] } = context;
      const sessionId = sessionContext?.sessionId;

      // Get conversation history from session memory
      const conversationMessages = this.formatConversationWithDatabaseContext(
        sessionId, 
        userMessage, 
        databaseResults
      );
      
      console.log('🤖 Sending database-first conversation:', {
        sessionId,
        messageCount: conversationMessages?.length,
        hasDatabaseResults: !!databaseResults,
        resultsCount: databaseResults?.length || 0
      });
      
      // Create database-first system prompt
      const systemPrompt = this.getDatabaseFirstSystemPrompt({
        ...context,
        databaseResults,
        conversationHistory: this.getSessionMemory(sessionId)
      });
      
      let response = await this.makeAPIRequest({
        model: this.defaultModel,
        system: systemPrompt,
        messages: conversationMessages,
        max_tokens: this.maxTokens,
        temperature: this.temperature,
      });

      const claudeResponse = this.extractMessageContent(response?.data);
      
      if (claudeResponse) {
        // ✅ NEW: Store conversation in session memory  
        this.updateSessionMemory(sessionId, userMessage, claudeResponse, databaseResults);
        
        console.log('✅ Claude database-first response generated');
        return claudeResponse;
      }
      
      return this.getDatabaseFirstFallback(userMessage, context);
      
    } catch (error) {
      console.error('Claude AI Error:', error);
      return this.getDatabaseFirstFallback(userMessage, context);
    }
  }

  // ✅ NEW: Database-first fallback responses (no external redirects)
  getDatabaseFirstFallback(userMessage, context = {}) {
    const { databaseResults = null, sessionContext = {} } = context;
    const agentPhone = context?.agentPhone || '(916) 833-0052';
    
    // If we have database results, use them
    if (databaseResults && Array.isArray(databaseResults) && databaseResults?.length > 0) {
      return this.formatDatabaseResults(databaseResults, agentPhone);
    }
    
    // Handle search queries with no results
    if (this.isSearchQuery(userMessage)) {
      return `I searched our database system but wasn't able to find them in our current records. This could mean:

• Still being processed (can take 2-4 hours after arrest)
• At a different facility outside Sacramento County  
• Name spelling might be different than expected
• Already released recently

Would you like me to connect you with Damian directly at ${agentPhone} for immediate assistance? I can also try searching with different name variations if you'd like.`;
    }
    
    // Handle greeting/general inquiries
    if (this.isGreeting(userMessage)) {
      return `Hello! I'm here to help you with bail bonds and inmate searches in Sacramento County. I have immediate access to our database covering both major facilities:

• Sacramento County Main Jail
• Rio Cosumnes Correctional Center

How can I help you today? You can give me a name to search for, or ask about the bail bond process. For immediate assistance, call ${agentPhone}.`;
    }
    
    // Default database-first response
    return `I'm here to help with bail bonds and inmate information using our Sacramento County database. Whether you need to locate someone or understand the bail process, I can provide specific guidance.

What can I help you with today? For immediate assistance, call ${agentPhone}.`;
  }

  // ✅ NEW: Format database results in structured table format
  formatDatabaseResults(results, agentPhone) {
    if (!results || results?.length === 0) {
      return `I wasn't able to find them in my system. Would you like me to connect you with Damian directly at ${agentPhone}?`;
    }

    let response = `✅ I found ${results?.length} person${results?.length !== 1 ? 's' : ''} in Sacramento County:\n\n`;
    
    // Create structured table format
    response += '**SEARCH RESULTS:**\n\n';
    
    results?.forEach((person, index) => {
      response += `**${index + 1}. ${person?.first_name || ''} ${person?.middle_name || ''} ${person?.last_name || ''}**\n`;
      if (person?.dob) response += `DOB: ${person?.dob}\n`;
      response += `Status: ${person?.current_status || 'Unknown'}\n`;
      if (person?.current_facilities?.length > 0) {
        response += `Facility: ${person?.current_facilities?.join(', ')}\n`;
      }
      if (person?.last_arrest_datetime) {
        response += `Last Arrest: ${new Date(person.last_arrest_datetime)?.toLocaleDateString()}\n`;
      }
      response += '\n';
    });
    
    response += `**Next Steps:**\n`;
    response += `• If you see the right person, I can help start the bail process immediately\n`;
    response += `• California bail bonds are 10% of the court-set bail amount\n`;
    response += `• Call ${agentPhone} for immediate processing\n\n`;
    response += `Which person matches who you're looking for?`;
    
    return response;
  }

  // ✅ NEW: Helper methods for intent detection
  isSearchQuery(message) {
    const searchKeywords = ['find', 'search', 'look for', 'arrested', 'jail', 'custody', 'booking'];
    return searchKeywords?.some(keyword => message?.toLowerCase()?.includes(keyword));
  }

  isGreeting(message) {
    const greetingKeywords = ['hello', 'hi', 'hey', 'good morning', 'good afternoon'];
    return greetingKeywords?.some(keyword => message?.toLowerCase()?.includes(keyword));
  }

  // ✅ ENHANCED: API request handling with better error management
  async makeAPIRequest(requestBody, usesFallback = false) {
    try {
      return await axios?.post(
        `${this.baseURL}/v1/messages`,
        requestBody,
        {
          headers: this.getHeaders(),
          timeout: 25000, // ✅ REDUCED: Shorter timeout for better UX
        }
      );
    } catch (error) {
      if (!usesFallback && (error?.response?.status === 404 || error?.response?.status === 400)) {
        console.log(`Primary model failed (${error?.response?.status}), trying fallback model`);
        const fallbackBody = { ...requestBody, model: this.fallbackModel };
        return await this.makeAPIRequest(fallbackBody, true);
      }
      throw error;
    }
  }

  // Enhanced message content extraction
  extractMessageContent(responseData) {
    try {
      if (responseData?.content && responseData?.content?.length > 0) {
        const textContent = responseData?.content?.find(item => item?.type === 'text');
        return textContent?.text || null;
      }
      return null;
    } catch (error) {
      console.error('Error extracting message content:', error);
      return null;
    }
  }

  // ✅ NEW: Session cleanup utility
  cleanupExpiredSessions() {
    const now = Date.now();
    for (const [sessionId, sessionData] of this.sessionMemory?.entries()) {
      if (now - sessionData?.lastAccessed > this.maxSessionAge) {
        this.sessionMemory?.delete(sessionId);
      }
    }
  }

  // ✅ NEW: Connection test with database-first validation
  async testConnection() {
    if (!this.isConfigured()) {
      return { success: false, error: 'API key not configured' };
    }

    try {
      let response = await this.makeAPIRequest({
        model: this.defaultModel,
        system: "You are a bail bond agent assistant. Always use database information provided. Never redirect users to external websites.",
        messages: [
          {
            role: 'user',
            content: 'Test database-first response capability.'
          }
        ],
        max_tokens: 50,
      });

      return { 
        success: true, 
        model: this.defaultModel,
        message: 'Claude AI configured for database-first responses' 
      };
    } catch (error) {
      console.error('Claude connection test failed:', error);
      return { success: false, error: error?.message };
    }
  }
}

// Export singleton instance
export const claudeAIService = new ClaudeAIService();
export default claudeAIService;